import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { UserMinus, Crown, UserPlus } from "lucide-react";
import TeamManagementDropdown from "./TeamManagementDropdown";
import InviteTeamMemberDialog from "./InviteTeamMemberDialog";
import PendingInvitationsCard from "./PendingInvitationsCard";

interface TeamMember {
  id: string;
  user_id: string;
  role: string;
  joined_at: string;
  email?: string;
}

interface TeamMembersListProps {
  challengeId: string;
  teamId?: string;
  teamName?: string;
  isTeamLead: boolean;
  onUpdate?: () => void;
  onTeamDeleted?: () => void;
}

const TeamMembersList: React.FC<TeamMembersListProps> = ({
  challengeId,
  teamId,
  teamName,
  isTeamLead,
  onUpdate,
  onTeamDeleted,
}) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserName, setCurrentUserName] = useState<string>("Team Lead");
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  useEffect(() => {
    fetchMembers();
    getCurrentUser();
  }, [challengeId]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
    
    if (user?.id) {
      // Fetch display name for notifications
      const { data: profile } = await supabase
        .from("contributor_profiles")
        .select("display_name")
        .eq("id", user.id)
        .single();
      
      if (profile?.display_name) {
        setCurrentUserName(profile.display_name);
      } else {
        // Fallback to email
        const { data: userProfile } = await supabase
          .from("profiles")
          .select("email")
          .eq("id", user.id)
          .single();
        setCurrentUserName(userProfile?.email?.split("@")[0] || "Team Lead");
      }
    }
  };

  const fetchMembers = async () => {
    try {
      const { data: membersData, error: membersError } = await supabase
        .from("challenge_team_members")
        .select("id, user_id, role, joined_at")
        .eq("challenge_id", challengeId)
        .order("joined_at", { ascending: true });

      if (membersError) throw membersError;

      // Fetch emails from profiles
      const userIds = membersData?.map((m) => m.user_id) || [];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, email")
        .in("id", userIds);

      const profileMap = new Map(profiles?.map((p) => [p.id, p.email]));

      const enrichedMembers = (membersData || []).map((m) => ({
        ...m,
        email: profileMap.get(m.user_id) || "Unknown",
      }));

      setMembers(enrichedMembers);
    } catch (error) {
      console.error("Error fetching team members:", error);
      toast.error("Failed to load team members");
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (memberId: string, _userId: string) => {
    if (!confirm("Are you sure you want to remove this team member?")) return;

    try {
      const { error } = await supabase
        .from("challenge_team_members")
        .delete()
        .eq("id", memberId);

      if (error) throw error;

      toast.success("Team member removed");
      fetchMembers();
      onUpdate?.();
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error("Failed to remove team member");
    }
  };

  const leaveTeam = async () => {
    if (!currentUserId) return;
    if (!confirm("Are you sure you want to leave this challenge?")) return;

    try {
      const { error } = await supabase
        .from("challenge_team_members")
        .delete()
        .eq("challenge_id", challengeId)
        .eq("user_id", currentUserId);

      if (error) throw error;

      toast.success("You have left the team");
      onUpdate?.();
    } catch (error) {
      console.error("Error leaving team:", error);
      toast.error("Failed to leave team");
    }
  };

  const getInitials = (email: string) => {
    return email.slice(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No team members yet. Be the first to claim this challenge!
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pending Invitations for Lead */}
      {isTeamLead && teamId && (
        <PendingInvitationsCard
          teamId={teamId}
          onUpdate={() => {
            fetchMembers();
            onUpdate?.();
          }}
        />
      )}

      {/* Team Management Header */}
      {teamId && teamName && currentUserId && (
        <div className="flex justify-between items-center pb-2 border-b">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowInviteDialog(true)}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Team Member
          </Button>
          {isTeamLead && (
            <TeamManagementDropdown
              teamId={teamId}
              teamName={teamName}
              currentUserId={currentUserId}
              challengeId={challengeId}
              leaderName={currentUserName}
              onUpdate={() => {
                fetchMembers();
                onUpdate?.();
              }}
              onTeamDeleted={() => {
                onTeamDeleted?.();
              }}
            />
          )}
        </div>
      )}

      {/* Member List */}
      <div className="space-y-3">
        {members.map((member) => (
          <div
            key={member.id}
            className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
          >
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarFallback className="bg-primary/20 text-primary">
                  {getInitials(member.email || "")}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">
                    {member.email}
                  </span>
                  {member.role === "lead" && (
                    <Badge variant="outline" className="text-xs gap-1">
                      <Crown className="h-3 w-3" />
                      Lead
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  Joined {new Date(member.joined_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              {member.user_id === currentUserId && member.role !== "lead" && (
                <Button variant="ghost" size="sm" onClick={leaveTeam}>
                  Leave
                </Button>
              )}
              {isTeamLead && member.user_id !== currentUserId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeMember(member.id, member.user_id)}
                  className="text-destructive hover:text-destructive"
                >
                  <UserMinus className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Invite Dialog */}
      {teamId && teamName && (
        <InviteTeamMemberDialog
          open={showInviteDialog}
          onOpenChange={setShowInviteDialog}
          teamId={teamId}
          teamName={teamName}
          challengeId={challengeId}
          isTeamLead={isTeamLead}
          onInviteSent={() => {
            fetchMembers();
            onUpdate?.();
          }}
        />
      )}
    </div>
  );
};

export default TeamMembersList;
